import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";
import { classifyOrgs, fetchOrgGraph, type OrgClassification } from "../../lib/github-orgs.js";
import {
  enterpriseSingleNotice, enterpriseMultiplePrompt, noEnterpriseNotice,
  undeterminedNotice, noOrgsRefusal,
} from "../../lib/github-orgs-copy.js";

export class BrainOrgsCommand extends M8tCommand {
  static paths = [["brain", "orgs"]];
  static usage = Command.Usage({
    description: "Show which GitHub org your workers' brains should live in.",
    details:
      "Read-only. Lists the organizations you belong to and prefers one linked to a GitHub Enterprise account. Advisory: `m8t brain app-create --org <org>` decides where the App is installed, so an explicit choice always wins.",
    examples: [
      ["Show the recommendation", "$0 brain orgs"],
      ["Machine-readable", "$0 brain orgs --output json"],
    ],
  });

  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    // Normalize output: clipanion leaves a descriptor object on the property
    // until argv parsing; when set directly in tests (or not supplied), treat
    // anything that is not a plain string as "pretty" (our diagnostic default).
    const outputFlag =
      this.output === "json" || this.output === "auto" || this.output === "pretty"
        ? this.output
        : "pretty";
    const mode = resolveOutputMode(outputFlag, this.context.stdout as NodeJS.WriteStream);

    const classification = classifyOrgs(await fetchOrgGraph());

    if (mode === "json") {
      this.context.stdout.write(renderJson(classification) + "\n");
      return exitCodeFor(classification);
    }

    this.renderPretty(classification);
    return exitCodeFor(classification);
  }

  private renderPretty(c: OrgClassification): void {
    const out = (s: string) => this.context.stdout.write(s + "\n");

    switch (c.verdict) {
      case "enterprise-single":
        out(`${colors.success("✓")} ${enterpriseSingleNotice(c.selected ?? "")}`);
        return;
      case "enterprise-multiple":
        out(enterpriseMultiplePrompt(c.candidates.map((o) => o.login)));
        return;
      case "no-enterprise":
        out(noEnterpriseNotice(c.candidates[0]?.login ?? ""));
        if (c.candidates.length > 1) {
          out(`  ${colors.dim(`orgs: ${c.candidates.map((o) => o.login).join(", ")}`)}`);
        }
        return;
      case "undetermined":
        out(undeterminedNotice());
        return;
      case "no-orgs":
        this.context.stderr.write(`${colors.error("✗")} ${noOrgsRefusal()}\n`);
        return;
    }
  }
}

/**
 * Only "no-orgs" is a stop. Every other verdict - including a failed check -
 * leaves the caller free to continue, because org selection is an enhancement
 * over asking, never a gate in front of it.
 */
function exitCodeFor(c: OrgClassification): number {
  return c.verdict === "no-orgs" ? 1 : 0;
}
